import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { base_url } from "../utils/constante";

const API_URL = base_url;

// 🔑 Racine du domaine dérivée de base_url (pas codée en dur "thrundrz.fr")
//    → fonctionne aussi bien en prod qu'en preprod.
const ORIGIN = (() => {
    try {
        return new URL(base_url).origin;
    } catch (_) {
        return "https://thrundrz.fr";
    }
})();

// 🔑 Parse le champ prixEven pour éviter d'afficher le JSON brut
//    dans les cartes. Retourne un texte compact adapté à une carte.
function formatPrixCarte(event) {
    if (event.isGratuit === true || event.isGratuit === 1 || event.isGratuit === "1") {
        return null; // déjà affiché via le badge "Gratuit"
    }

    const raw = (event.prixEven ?? "").toString().trim();
    if (!raw || raw === "0") return null;

    // Tarifs multiples : [{"titre":"Adulte","prix":"20.45"}, ...]
    if (raw.startsWith("[")) {
        try {
            const tarifs = JSON.parse(raw)
                .filter((t) => t && t.titre)
                .map((t) => parseFloat(t.prix) || 0);

            if (tarifs.length === 0) return null;
            if (tarifs.length === 1) return `${tarifs[0].toFixed(2)} €`;

            const min = Math.min(...tarifs);
            return `Dès ${min.toFixed(2)} €`;
        } catch (_) {
            return null;
        }
    }

    // Ancien format : juste un nombre
    const val = parseFloat(raw);
    if (!isNaN(val) && val > 0) {
        return `${val.toFixed(2)} €`;
    }

    return null;
}

const EventsHome = () => {
    const [events, setEvents] = useState([]);

    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Nombre d'événements affichés par page.
     *
     * Attention :
     * c'est normalement le BACKEND qui décide du nombre
     * via sa pagination.
     */
    const getEvents = async (pageNumber = 1) => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(
                `${API_URL}evenementExplorer`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type": "application/json",
                        // 🔑 Web = toujours JSON brut pour les tarifs
                        //    (pas de contrainte de review comme l'app mobile)
                        "X-App-Client": "web-react",
                    },

                    body: JSON.stringify({
                        page: pageNumber,

                        // Toutes les catégories
                        categoryId: -1,

                        // Aucun filtre sur la Home
                        tri: "date",
                        prix: "tous",
                        quand: "tous",
                        acces: [],
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Erreur API: ${response.status} `
                );
            }

            const json = await response.json();

            console.log(
                "📡 Réponse evenementExplorer :",
                json
            );

            const eventsData = Array.isArray(json.events)
                ? json.events
                : [];

            setEvents(eventsData);

            /**
             * Récupération de la pagination Laravel
             */
            setPagination({
                current_page:
                    json.pagination?.current_page ??
                    json.pagination?.currentPage ??
                    pageNumber,

                last_page:
                    json.pagination?.last_page ??
                    json.pagination?.lastPage ??
                    1,

                total:
                    json.pagination?.total ??
                    eventsData.length,
            });

        } catch (err) {
            console.error(
                "❌ Erreur récupération événements :",
                err
            );

            setError(
                "Impossible de charger les événements."
            );

            setEvents([]);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Chargement initial
     */
    useEffect(() => {
        getEvents(1);
    }, []);

    /**
     * Changement de page
     */
    const handlePageChange = (newPage) => {
        if (loading) {
            return;
        }

        if (newPage < 1) {
            return;
        }

        if (newPage > pagination.last_page) {
            return;
        }

        getEvents(newPage);

        /**
         * Retour en haut de la section événements
         */
        const element =
            document.getElementById(
                "scroller_events_home"
            );

        if (element) {
            element.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    };

    /**
     * Gestion des images
     * 🔑 Utilise ORIGIN (dérivé de base_url) au lieu de "thrundrz.fr"
     *    codé en dur → fonctionne en prod ET en preprod.
     */
    const getImageUrl = (event) => {
        if (
            !event.imgEven ||
            event.imgEven === "empty"
        ) {
            return "/images/event-placeholder.jpg";
        }

        /**
         * L'API retourne déjà une URL complète
         */
        if (
            event.imgEven.startsWith("http://") ||
            event.imgEven.startsWith("https://")
        ) {
            return event.imgEven;
        }

        /**
         * Sinon on construit l'URL storage à partir du domaine
         * courant (preprod ou prod selon base_url)
         */
        return `${ORIGIN}/backendpublic/public/storage/${event.imgEven}`;
    };

    /**
     * Skeleton d'une carte
     */
    const EventSkeleton = () => {
        return (
            <div className="overflow-hidden rounded-2xl bg-zinc-900 animate-pulse">

                <div className="h-56 w-full bg-zinc-800" />

                <div className="p-5">

                    <div className="h-5 w-3/4 rounded bg-zinc-800" />

                    <div className="mt-3 h-4 w-1/2 rounded bg-zinc-800" />

                    <div className="mt-5 h-4 w-1/3 rounded bg-zinc-800" />

                    <div className="mt-3 h-4 w-2/3 rounded bg-zinc-800" />

                    <div className="mt-6 h-4 w-1/2 rounded bg-zinc-800" />

                </div>
            </div>
        );
    };

    return (
        <section
            id="scroller_events_home"
            className="w-full bg-black px-4 py-16 sm:px-6 lg:px-12"
        >

            <div className="mx-auto max-w-7xl">

                {/* ================================= */}
                {/* HEADER */}
                {/* ================================= */}

                <div className="mb-10 text-center">

                    <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-purple-400">
                        Découvrez
                    </p>

                    <h2 className="text-3xl font-bold text-white md:text-4xl">
                        Les événements à ne pas manquer
                    </h2>

                    <p className="mx-auto mt-4 max-w-2xl text-gray-400">
                        Découvrez les événements près de chez vous :
                        concerts, spectacles, sport, sorties et bien plus encore.
                    </p>

                </div>

                {/* ================================= */}
                {/* ERREUR */}
                {/* ================================= */}

                {error && !loading && (
                    <div className="flex flex-col items-center justify-center py-16">

                        <p className="text-center text-red-400">
                            {error}
                        </p>

                        <button
                            onClick={() =>
                                getEvents(
                                    pagination.current_page
                                )
                            }
                            className="mt-5 rounded-xl bg-purple-600 px-6 py-3 font-semibold text-white transition hover:bg-purple-700"
                        >
                            Réessayer
                        </button>

                    </div>
                )}

                {/* ================================= */}
                {/* LOADING */}
                {/* ================================= */}

                {loading && (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">

                        {Array.from({ length: 12 }).map(
                            (_, index) => (
                                <EventSkeleton
                                    key={index}
                                />
                            )
                        )}

                    </div>
                )}

                {/* ================================= */}
                {/* ÉVÉNEMENTS */}
                {/* ================================= */}

                {!loading &&
                    !error &&
                    events.length > 0 && (
                        <>

                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">

                                {events.map((event) => (

                                    <Link
                                        key={event.id}
                                        to={`/evenement/${event.id}`}
                                        className="group overflow-hidden rounded-2xl bg-zinc-900 transition duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-purple-900/30"
                                    >

                                        {/* ========================= */}
                                        {/* IMAGE */}
                                        {/* ========================= */}

                                        <div className="relative h-56 w-full overflow-hidden bg-zinc-800">

                                            <img
                                                src={getImageUrl(event)}
                                                alt={
                                                    event.nomEven ||
                                                    "Événement Thrundrz"
                                                }
                                                className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                                loading="lazy"
                                                onError={(e) => {
                                                    e.currentTarget.src =
                                                        "/images/event-placeholder.jpg";
                                                }}
                                            />

                                            {/* Catégorie */}

                                            {event.nomCat && (
                                                <div className="absolute left-3 top-3 rounded-full bg-black/70 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                                                    {event.nomCat}
                                                </div>
                                            )}

                                            {/* Gratuit */}

                                            {(event.isGratuit === true ||
                                                event.isGratuit === 1 ||
                                                event.isGratuit === "1") && (
                                                    <div className="absolute right-3 top-3 rounded-full bg-green-500 px-3 py-1 text-xs font-bold text-white">
                                                        Gratuit
                                                    </div>
                                                )}

                                        </div>

                                        {/* ========================= */}
                                        {/* CONTENU */}
                                        {/* ========================= */}

                                        <div className="p-5">

                                            <h3 className="line-clamp-2 text-lg font-bold text-white transition group-hover:text-purple-400">
                                                {event.nomEven ||
                                                    "Événement sans titre"}
                                            </h3>

                                            {event.sousNomEven && (
                                                <p className="mt-1 line-clamp-1 text-sm text-gray-400">
                                                    {event.sousNomEven}
                                                </p>
                                            )}

                                            {/* DATE */}

                                            {event.dateEven && (
                                                <p className="mt-4 text-sm font-medium text-purple-400">
                                                    {event.dateEven}
                                                </p>
                                            )}

                                            {/* LOCALISATION */}

                                            {(event.villeEven ||
                                                event.codePostalEven) && (
                                                    <p className="mt-2 text-sm text-gray-400">

                                                        {event.villeEven}

                                                        {event.codePostalEven
                                                            ? ` · ${event.codePostalEven}`
                                                            : ""}

                                                    </p>
                                                )}

                                            {/* PRIX — parsé, jamais le JSON brut */}

                                            {formatPrixCarte(event) && (
                                                <p className="mt-2 text-sm font-semibold text-purple-300">
                                                    {formatPrixCarte(event)}
                                                </p>
                                            )}

                                            {/* CTA */}

                                            <div className="mt-5 flex items-center justify-between">

                                                <span className="text-sm font-semibold text-white transition group-hover:text-purple-400">
                                                    Voir l'événement
                                                </span>

                                                <span className="text-xl text-purple-400 transition duration-300 group-hover:translate-x-1">
                                                    →
                                                </span>

                                            </div>

                                        </div>

                                    </Link>

                                ))}

                            </div>

                            {/* ================================= */}
                            {/* PAGINATION */}
                            {/* ================================= */}

                            {pagination.last_page > 1 && (

                                <div
                                    className="mt-10 flex items-center justify-center gap-4"
                                >

                                    {/* PRÉCÉDENT */}

                                    <button
                                        disabled={
                                            pagination.current_page ===
                                            1 ||
                                            loading
                                        }
                                        onClick={() =>
                                            handlePageChange(
                                                pagination.current_page -
                                                1
                                            )
                                        }
                                        className={`
                                            rounded-xl
                                            lg:px-6 px-2
                                             py-2.5
                                            font-semibold
                                            text-white
                                            transition
                                            ${pagination.current_page === 1
                                                ? "cursor-not-allowed bg-gray-700 opacity-50"
                                                : "bg-purple-600 hover:bg-purple-700"
                                            }
                                        `}
                                    >
                                        Précédent
                                    </button>

                                    {/* PAGE X SUR Y */}

                                    <span className="min-w-[140px] text-center text-lg font-medium text-white">

                                        Page{" "}

                                        {pagination.current_page}

                                        {" "}sur{" "}

                                        {pagination.last_page}

                                    </span>

                                    {/* SUIVANT */}

                                    <button
                                        disabled={
                                            pagination.current_page ===
                                            pagination.last_page ||
                                            loading
                                        }
                                        onClick={() =>
                                            handlePageChange(
                                                pagination.current_page +
                                                1
                                            )
                                        }
                                        className={`
                                            rounded-xl
                                            lg:px-6 px-2
                                            py-2.5
                                            font-semibold
                                            text-white
                                            transition
                                            ${pagination.current_page ===
                                                pagination.last_page
                                                ? "cursor-not-allowed bg-gray-700 opacity-50"
                                                : "bg-purple-600 hover:bg-purple-700"
                                            }
                                        `}
                                    >
                                        Suivant
                                    </button>

                                </div>
                            )}

                            {/* ================================= */}
                            {/* TOTAL */}
                            {/* ================================= */}

                            {pagination.total > 0 && (

                                <p className="mt-5 text-center text-sm text-gray-500">

                                    {pagination.total} événement
                                    {pagination.total > 1
                                        ? "s"
                                        : ""}

                                </p>

                            )}

                        </>
                    )}

                {/* ================================= */}
                {/* AUCUN ÉVÉNEMENT */}
                {/* ================================= */}

                {!loading &&
                    !error &&
                    events.length === 0 && (

                        <div className="py-16 text-center">

                            <p className="text-lg text-gray-400">
                                Aucun événement disponible pour le moment.
                            </p>

                        </div>

                    )}

            </div>

        </section>
    );
};

export default EventsHome;